import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Select, Input, Button, Space, Typography, Alert,
  Card, message, Checkbox, Divider, Descriptions, Tag
} from 'antd';
import {
  SwapOutlined, UserOutlined, WarningOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import { appointmentApi, Appointment } from '../../services/appointmentApi';
import { apiRoot } from '../../services/apiInstances';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface TechnicianChangeModalProps {
  visible: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
  email: string;
}

export const TechnicianChangeModal: React.FC<TechnicianChangeModalProps> = ({
  visible,
  appointment,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [showConflicts, setShowConflicts] = useState(false);

  useEffect(() => {
    if (visible && appointment) {
      loadTechnicians();
      form.setFieldsValue({
        notify_customer: true,
        manual_override: false
      });
    }
  }, [visible, appointment]);

  const loadTechnicians = async () => {
    setLoadingTechnicians(true);
    try {
      const [techniciansResponse, ownersResponse] = await Promise.all([
        apiRoot.get('/users', {
          params: { role: 'technician', status: 'active' }
        }),
        apiRoot.get('/users', {
          params: { role: 'owner', status: 'active' }
        })
      ]);

      const allUsers = [...techniciansResponse.data, ...ownersResponse.data].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setTechnicians(allUsers);
    } catch (error) {
      console.error('Error loading technicians:', error);
      message.error('Failed to load technicians');
    } finally {
      setLoadingTechnicians(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!appointment?.id) return;

    if (values.new_technician_id === appointment.technician_id) {
      message.warning('Please select a different technician');
      return;
    }

    setSubmitting(true);
    try {
      // TODO: Get actual user ID from auth context
      const userId = 1; // Default to admin user

      const result = await appointmentApi.changeTechnician(appointment.id, {
        new_technician_id: values.new_technician_id,
        reason: values.reason,
        notify_customer: values.notify_customer !== false,
        changed_by_user_id: userId,
        manual_override: values.manual_override || false,
        override_reason: values.override_reason
      });

      message.success('Technician changed successfully');
      form.resetFields();
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error changing technician:', error);

      if (error.response?.status === 409) {
        // Conflict detected
        const conflictData = error.response.data;
        setConflicts(conflictData.conflicts || []);
        setShowConflicts(true);

        if (conflictData.can_override) {
          message.warning('Conflicts detected. You can enable manual override to proceed.');
        } else {
          message.error('Cannot change technician due to critical conflicts.');
        }
      } else if (error.response?.status === 400) {
        message.error(error.response.data.error || 'Invalid request');
      } else {
        message.error('Failed to change technician');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setConflicts([]);
    setShowConflicts(false);
    onClose();
  };

  if (!appointment) return null;

  return (
    <Modal
      title={
        <Space>
          <SwapOutlined />
          <span>Change Technician</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={null}
    >
      {/* Current Appointment Info */}
      <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Appointment">
            {appointment.title}
          </Descriptions.Item>
          <Descriptions.Item label="Date & Time">
            {dayjs(appointment.start_time).format('MMMM DD, YYYY @ h:mm A')}
          </Descriptions.Item>
          <Descriptions.Item label="Current Technician">
            <Tag icon={<UserOutlined />} color="blue">
              {appointment.technician_name || 'Unknown'}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Title level={5}>Select New Technician</Title>

        <Form.Item
          label="New Technician"
          name="new_technician_id"
          rules={[{ required: true, message: 'Please select a technician' }]}
        >
          <Select
            placeholder="Select technician"
            showSearch
            loading={loadingTechnicians}
            filterOption={(input, option) => {
              const label = `${option?.label || ''}`.toLowerCase();
              return label.includes(input.toLowerCase());
            }}
            options={technicians
              .filter(tech => tech.id !== appointment.technician_id)
              .map(tech => ({
                value: tech.id,
                label: `${tech.first_name} ${tech.last_name} (${tech.role})`
              }))}
          />
        </Form.Item>

        <Form.Item
          label="Reason for Change"
          name="reason"
        >
          <TextArea
            rows={3}
            placeholder="e.g., Original technician unavailable, reassignment for expertise, workload balancing..."
          />
        </Form.Item>

        <Form.Item
          name="notify_customer"
          valuePropName="checked"
        >
          <Checkbox defaultChecked>
            Notify customer about technician change
          </Checkbox>
        </Form.Item>

        {/* Conflicts Alert */}
        {showConflicts && conflicts.length > 0 && (
          <>
            <Alert
              message="Scheduling Conflicts Detected"
              description={
                <div>
                  {conflicts.map((conflict, index) => (
                    <div key={index}>
                      <Tag color={
                        conflict.conflict_severity === 'critical' ? 'red' :
                        conflict.conflict_severity === 'high' ? 'orange' :
                        conflict.conflict_severity === 'medium' ? 'gold' : 'blue'
                      }>
                        {conflict.conflict_severity}
                      </Tag>
                      {conflict.description}
                    </div>
                  ))}
                </div>
              }
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              name="manual_override"
              valuePropName="checked"
            >
              <Checkbox>
                Override conflicts (I understand the risks)
              </Checkbox>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.manual_override !== currentValues.manual_override
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('manual_override') ? (
                  <Form.Item
                    label="Override Reason"
                    name="override_reason"
                    rules={[{ required: true, message: 'Please provide override reason' }]}
                  >
                    <TextArea
                      rows={2}
                      placeholder="Explain why you're overriding the conflict..."
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </>
        )}

        <Divider />

        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            icon={<CheckCircleOutlined />}
            loading={submitting}
          >
            Change Technician
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};
